import useWebAnimations from '@wellyshen/use-web-animations';
import { useCallback, useRef, useState } from 'react';

const EASE_IN = 'cubic-bezier( 0.6, 0.46, 0.5, 0.87 )';
const EASE_OUT = 'cubic-bezier( 0.66, 0.1, 0.48, 0.7 )';
const EASE_IN_OUT = 'cubic-bezier( 0.7, 0.08, 0.3, 0.95 )';
const angle = 1;
const scale = 1.01;

const ANIMATION_DURATION = 750;

/**
 * Wiggles an element.
 *
 * @returns a [ref, wiggle, wiggling] tuple where `ref` should be attached to the element
 *   you want to wiggle, `wiggle` is a function you can call to trigger the animation,
 *   and `wiggling` is true while the animation is playing.  Repeated calls to
 *   `wiggle` will extend the animation time.
 */
export function useWiggle<T extends HTMLElement | null>(): [
  React.RefObject<T>,
  (duration?: number) => void,
  boolean,
] {
  const animateUntil = useRef<number | undefined>(undefined);
  const [wiggling, setWiggling] = useState(false);

  const { ref, getAnimation } = useWebAnimations<T>({
    autoPlay: false,
    keyframes: [
      {
        transform: 'rotate3d(0,0,1,0) scale(1)',
        easing: EASE_IN,
      },
      {
        transform: `rotate3d(0,0,1,-${angle}deg) scale(${scale})`,
        easing: EASE_IN_OUT,
      },
      {
        transform: `rotate3d(0,0,1,${angle}deg) scale(${scale})`,
        easing: EASE_IN_OUT,
      },
      {
        transform: `rotate3d(0,0,1,-${angle}deg) scale(${scale})`,
        easing: EASE_IN_OUT,
      },
      {
        transform: `rotate3d(0,0,1,${angle}deg) scale(${scale})`,
        easing: EASE_IN_OUT,
      },
      {
        transform: 'rotate3d(0,0,1,0) scale(1)',
        easing: EASE_OUT,
      },
    ],

    animationOptions: {
      duration: ANIMATION_DURATION,
      fill: 'forwards',
    },

    onUpdate: ({ playState }) => {
      if (playState === 'finished') {
        if (!animateUntil.current || Date.now() >= animateUntil.current - 100) {
          // Let the animation finish.
          setWiggling(false);
        } else {
          // Play it again!
          getAnimation()?.play();
        }
      }
    },
  });

  const wiggle = useCallback(
    (duration?: number) => {
      animateUntil.current = Date.now() + (duration ?? ANIMATION_DURATION);
      getAnimation()?.play();
      setWiggling(true);
    },
    [getAnimation],
  );

  return [ref, wiggle, wiggling];
}
